import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { Texture } from '@babylonjs/core/Materials/Textures/texture';
import type { Scene } from '@babylonjs/core/scene';

/**
 * Cached canvas-drawn textures for the map. Procedural + tileable so we get
 * facades / road / concrete detail with zero external asset weight and a tiny
 * material count (one texture → one material → merged draw call). Same
 * DynamicTexture approach already used for player name tags in character.ts.
 */
const cache = new Map<string, DynamicTexture>();

/** HMR safety — drop cached textures when the scene is rebuilt. */
export function clearTextureCache(): void {
  for (const t of cache.values()) t.dispose();
  cache.clear();
}

function make(
  scene: Scene,
  key: string,
  w: number,
  h: number,
  draw: (ctx: CanvasRenderingContext2D, w: number, h: number) => void,
  tile = true,
): DynamicTexture {
  const cached = cache.get(key);
  if (cached) return cached;
  const t = new DynamicTexture(key, { width: w, height: h }, scene, true);
  const ctx = t.getContext() as unknown as CanvasRenderingContext2D;
  draw(ctx, w, h);
  t.update();
  if (tile) {
    t.wrapU = Texture.WRAP_ADDRESSMODE;
    t.wrapV = Texture.WRAP_ADDRESSMODE;
  }
  // Anisotropic filtering tames the moiré shimmer on fine detail (window
  // mullions, paving seams) at grazing angles as the camera moves.
  t.anisotropicFilteringLevel = 8;
  cache.set(key, t);
  return t;
}

const rgb = (r: number, g: number, b: number): string => `rgb(${r},${g},${b})`;

/**
 * THE PALETTE in 0–255 (mirrors PALETTE in shared/map.ts). One cool
 * grey-lavender ramp + a single warm accent; every drawn texture stays inside
 * it so nothing drifts to green/yellow.
 */
const PAL = {
  white: [230, 230, 237],
  light: [199, 201, 216],
  mid: [153, 156, 176],
  slate: [112, 115, 138],
  dark: [69, 71, 92],
  ink: [36, 36, 46],
  accent: [204, 128, 66],
} as const;
const pal = (c: readonly [number, number, number]): string => rgb(c[0], c[1], c[2]);

/** Vertical sky gradient — cool grey-lavender overcast (palette sky tone). */
export function duskSkyTexture(scene: Scene): DynamicTexture {
  return make(
    scene,
    'sky-dusk',
    16,
    256,
    (ctx, w, h) => {
      const g = ctx.createLinearGradient(0, 0, 0, h);
      g.addColorStop(0.0, rgb(120, 123, 142)); // cool zenith
      g.addColorStop(0.55, rgb(150, 153, 172));
      g.addColorStop(0.82, rgb(180, 182, 198)); // pale haze band
      g.addColorStop(1.0, rgb(202, 203, 214)); // pale cool horizon
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, w, h);
    },
    false,
  );
}

/**
 * Tileable apartment facade with a 3×3 grid of windows + balcony bands per
 * tile (one tile = FACADE_TILE world units in each axis). Several tinted
 * variants give block-to-block variety (Caballito cream / beige / red-brown /
 * grey), assigned per building by world.ts.
 */
// The facade texture is a 3×3 window grid. We tile it at a DIFFERENT world
// density per axis so windows stay ~2u apart horizontally but floors are ~3u
// tall (square textures alone can't do that).
export const FACADE_GRID = 3;
export const FACADE_TILE_X = FACADE_GRID * 2.0; // 2.0u window pitch
export const FACADE_TILE_Y = FACADE_GRID * 3.0; // 3.0u floor height

interface FacadeVariant {
  wall: [number, number, number];
  balcony: [number, number, number];
  lit: number; // share of windows warm-lit (dusk)
}

// One cool lavender family (mirrors WALL_COLORS in shared/map.ts), light →
// slate. The lit windows are the lone warm spark (the accent); unlit windows
// are a cool dark — nothing green/yellow anywhere.
export const FACADE_VARIANTS: FacadeVariant[] = [
  { wall: [204, 206, 219], balcony: [170, 172, 188], lit: 0.07 }, // pale lavender
  { wall: [179, 181, 199], balcony: [150, 152, 170], lit: 0.08 },
  { wall: [156, 158, 179], balcony: [130, 132, 152], lit: 0.07 },
  { wall: [133, 135, 158], balcony: [110, 112, 134], lit: 0.09 },
  { wall: [168, 168, 184], balcony: [140, 140, 158], lit: 0.06 }, // neutral grey-lavender
  { wall: [115, 118, 142], balcony: [95, 98, 120], lit: 0.09 }, // slate
];

const mul = (c: [number, number, number], f: number): string =>
  rgb(Math.round(c[0] * f), Math.round(c[1] * f), Math.round(c[2] * f));

function drawFacade(v: FacadeVariant) {
  return (ctx: CanvasRenderingContext2D, w: number, h: number): void => {
    // Transparent background — the building mass behind supplies the wall color.
    // Only the structural elements (spandrel bands, window frames, glass) are
    // drawn opaque so that alpha-tested rendering makes side/back faces invisible.
    ctx.clearRect(0, 0, w, h);
    const N = FACADE_GRID;
    const cellX = w / N;
    const cellY = h / N;
    let n = 7;
    for (let r = 0; r < N; r++) {
      // Spandrel / floor band below each window row (reads as a darker slab line).
      const by = (r + 1) * cellY - cellY * 0.22;
      ctx.fillStyle = mul(v.wall, 0.82);
      ctx.fillRect(0, by, w, cellY * 0.22);
      ctx.fillStyle = mul(v.wall, 0.65);
      ctx.fillRect(0, by, w, 2);
      for (let c = 0; c < N; c++) {
        const padX = cellX * 0.3;
        const padTop = cellY * 0.16;
        const x = c * cellX + padX;
        const y = r * cellY + padTop;
        const ww = cellX - padX * 2;
        const wh = cellY * 0.55;
        // sill + lintel
        ctx.fillStyle = mul(v.wall, 0.6);
        ctx.fillRect(x - 2, y - 2, ww + 4, wh + 5);
        n = (n * 1103515245 + 12345) & 0x7fffffff;
        const isLit = n / 0x7fffffff < v.lit;
        ctx.fillStyle = isLit ? rgb(214, 150, 92) : rgb(52, 54, 72);
        ctx.fillRect(x, y, ww, wh);
        // mullions: one vertical, two horizontal (taller windows)
        ctx.fillStyle = mul(v.wall, 0.5);
        ctx.fillRect(x + ww / 2 - 1, y, 2, wh);
        ctx.fillRect(x, y + wh / 3 - 1, ww, 1.5);
        ctx.fillRect(x, y + (2 * wh) / 3 - 1, ww, 1.5);
      }
    }
  };
}

export function facadeTexture(scene: Scene, variant: number): DynamicTexture {
  const v = FACADE_VARIANTS[variant % FACADE_VARIANTS.length]!;
  return make(scene, `facade-${variant}`, 384, 384, drawFacade(v));
}

/** Tileable asphalt — pale cool grey so the ground reads bright (palette). */
export function asphaltTexture(scene: Scene): DynamicTexture {
  return make(scene, 'asphalt', 128, 128, (ctx, w, h) => {
    ctx.fillStyle = rgb(168, 170, 186);
    ctx.fillRect(0, 0, w, h);
    for (let i = 0; i < 800; i++) {
      const x = (i * 7919) % w;
      const y = (i * 104729) % h;
      const v = 158 + ((i * 13) % 26);
      ctx.fillStyle = rgb(v, v, v + 6);
      ctx.fillRect(x, y, 1, 1);
    }
  });
}

/** Tileable sidewalk paving — pale lavender-grey slabs with seam grid. */
export function sidewalkTexture(scene: Scene): DynamicTexture {
  return make(scene, 'sidewalk', 128, 128, (ctx, w, h) => {
    ctx.fillStyle = rgb(200, 201, 214);
    ctx.fillRect(0, 0, w, h);
    for (let i = 0; i < 400; i++) {
      const x = (i * 7919) % w;
      const y = (i * 65537) % h;
      const v = 190 + ((i * 11) % 22);
      ctx.fillStyle = `rgba(${v},${v},${v + 6},0.5)`;
      ctx.fillRect(x, y, 2, 2);
    }
    ctx.strokeStyle = 'rgba(0,0,0,0.2)';
    ctx.lineWidth = 2;
    for (let x = 0; x <= w; x += 64) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    for (let y = 0; y <= h; y += 64) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }
  });
}

/** "Av. Rivadavia" painted on the road (white, alpha) — non-tiling. */
export function roadTextTexture(scene: Scene): DynamicTexture {
  const t = make(
    scene,
    'roadtext',
    1024,
    256,
    (ctx, w, h) => {
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = 'rgba(225,225,228,0.8)';
      ctx.font = 'bold 150px ui-monospace, Menlo, monospace';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Av. Rivadavia', w / 2, h / 2);
    },
    false,
  );
  t.hasAlpha = true;
  return t;
}

/** See-through riot fence: opaque metal pickets + rails, TRANSPARENT gaps. */
export const FENCE_TILE_X = 2.2;
export const FENCE_TILE_Y = 2; // one panel = the 2u fence height
export function riotFenceTexture(scene: Scene): DynamicTexture {
  const t = make(scene, 'fence', 128, 128, (ctx, w, h) => {
    ctx.clearRect(0, 0, w, h); // transparent gaps → see-through
    const bar = pal(PAL.slate);
    const barHi = pal(PAL.light);
    // vertical pickets
    for (let x = 5; x < w; x += 16) {
      ctx.fillStyle = bar;
      ctx.fillRect(x, 0, 5, h);
      ctx.fillStyle = barHi;
      ctx.fillRect(x, 0, 2, h);
    }
    // horizontal rails (top / mid / bottom) + frame
    for (const ry of [0.08, 0.5, 0.92]) {
      const y = ry * h;
      ctx.fillStyle = bar;
      ctx.fillRect(0, y - 5, w, 10);
      ctx.fillStyle = barHi;
      ctx.fillRect(0, y - 5, w, 3);
    }
    ctx.strokeStyle = barHi;
    ctx.lineWidth = 6;
    ctx.strokeRect(3, 3, w - 6, h - 6);
  });
  t.hasAlpha = true;
  return t;
}

/** Newsstand / kiosk: slate body, a warm-accent sign band, serving window with
 *  rack panels — all kept inside the palette (no rainbow). Per side face. */
export function kioskTexture(scene: Scene): DynamicTexture {
  return make(scene, 'kiosk', 128, 128, (ctx, w, h) => {
    ctx.fillStyle = pal(PAL.slate); // slate metal body
    ctx.fillRect(0, 0, w, h);
    // vertical panel seams
    ctx.strokeStyle = 'rgba(0,0,0,0.28)';
    ctx.lineWidth = 2;
    for (let x = w / 4; x < w; x += w / 4) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, h);
      ctx.stroke();
    }
    // top sign band (the lone warm accent)
    ctx.fillStyle = pal(PAL.accent);
    ctx.fillRect(0, 0, w, h * 0.14);
    // serving-window opening (light) with rack panels
    ctx.fillStyle = pal(PAL.light);
    ctx.fillRect(w * 0.06, h * 0.34, w * 0.88, h * 0.30);
    const cols = [pal(PAL.mid), pal(PAL.light), pal(PAL.accent), pal(PAL.slate), pal(PAL.mid)];
    for (let row = 0; row < 2; row++) {
      for (let i = 0; i < 5; i++) {
        ctx.fillStyle = cols[(i + row * 2) % cols.length]!;
        ctx.fillRect(w * 0.08 + i * (w * 0.17), h * 0.36 + row * (h * 0.13), w * 0.13, h * 0.1);
      }
    }
    // counter shadow under the window
    ctx.fillStyle = 'rgba(0,0,0,0.32)';
    ctx.fillRect(0, h * 0.64, w, h * 0.05);
  });
}

/** Tileable concrete (platforms, walkways, parapets). */
export function concreteTexture(scene: Scene): DynamicTexture {
  return make(scene, 'concrete', 128, 128, (ctx, w, h) => {
    ctx.fillStyle = rgb(150, 152, 170);
    ctx.fillRect(0, 0, w, h);
    for (let i = 0; i < 500; i++) {
      const x = (i * 7919) % w;
      const y = (i * 65537) % h;
      const v = 140 + ((i * 11) % 28);
      ctx.fillStyle = `rgba(${v},${v},${v + 6},0.5)`;
      ctx.fillRect(x, y, 2, 2);
    }
    // faint seams
    ctx.strokeStyle = 'rgba(0,0,0,0.18)';
    ctx.lineWidth = 1;
    ctx.strokeRect(0.5, 0.5, w - 1, h - 1);
  });
}
